import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Button, EmptyState, Input, SearchInput, Skeleton, cn, useI18n } from '@xdocs/ui';
import { RoleGate } from '../../../features/admin/index.js';
import { CmsBlockEditor } from './CmsBlockEditor.js';
import { useCmsBlocks } from './useCmsBlocks.js';
import type { CmsBlockGroup } from './useCmsBlocks.js';
import { useCmsT } from './useCmsT.js';
import styles from './CmsPage.module.css';

function blockStatusLabel(
  group: CmsBlockGroup,
  t: ReturnType<typeof useCmsT>['t'],
): string {
  const row = group.en ?? group.ro;
  if (!row) return t('adminCms:blockList.status.missing');
  return t(`adminCms:blockList.status.${row.status}`, { version: row.version });
}

/**
 * `/admin/landing-cms` (F61) — landing block editor: a list of editable
 * blocks (keyed by `blockKey`) → per-locale Draft → Publish workflow
 * (`CmsBlockEditor.tsx`). `admin.cms.blocksList` isn't a paginated
 * `listQueryInput` resource (`useCmsBlocks.ts`), so this list is a plain
 * client-side-searched list (small, staff-curated set) rather than the
 * shared `<DataTable>` — still all four E19 states (loading/empty/error/
 * populated).
 *
 * Wrapped in `<RoleGate minimum="admin">` (`features/admin/`, sibling M4
 * task) — F61 is `admin`-only; see `PromosPage.tsx`'s doc comment for the
 * defense-in-depth rationale (UX-only, server enforces on every op).
 */
export function CmsPage(): ReactElement {
  const { t } = useCmsT();
  const { t: tCommon } = useI18n();
  const { groups, isLoading, isError, onRetry, invalidate } = useCmsBlocks();
  const [search, setSearch] = useState('');
  const [newBlockKey, setNewBlockKey] = useState('');
  const [openBlockKey, setOpenBlockKey] = useState<string | null>(null);

  const filteredGroups = useMemo(
    () => groups.filter((group) => group.blockKey.toLowerCase().includes(search.trim().toLowerCase())),
    [groups, search],
  );

  const openGroup: CmsBlockGroup | undefined =
    filteredGroups.find((g) => g.blockKey === openBlockKey) ?? (openBlockKey ? { blockKey: openBlockKey, en: undefined, ro: undefined } : undefined);

  return (
    <RoleGate minimum="admin">
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <h1 className={cn(styles['title'])}>{t('adminCms:page.title')}</h1>
        <p className={cn(styles['description'])}>{t('adminCms:page.description')}</p>
      </div>

      <div className={cn(styles['toolbar'])}>
        <SearchInput
          label={t('adminCms:blockList.title')}
          hideLabel
          placeholder={t('adminCms:blockList.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onClear={() => setSearch('')}
          clearLabel={tCommon('common:action.clearFilters')}
        />
        <form
          className={cn(styles['newBlockForm'])}
          onSubmit={(event) => {
            event.preventDefault();
            if (newBlockKey.trim().length === 0) return;
            setOpenBlockKey(newBlockKey.trim());
            setNewBlockKey('');
          }}
        >
          <Input
            label={t('adminCms:blockList.newBlock.label')}
            placeholder={t('adminCms:blockList.newBlock.placeholder')}
            value={newBlockKey}
            onChange={(event) => setNewBlockKey(event.target.value)}
          />
          <Button type="submit" variant="secondary" disabled={newBlockKey.trim().length === 0}>
            {t('adminCms:blockList.newBlock.add')}
          </Button>
        </form>
      </div>

      {isLoading && (
        <div className={cn(styles['skeletonList'])} aria-hidden="true">
          <Skeleton shape="row" count={5} />
        </div>
      )}

      {!isLoading && isError && (
        <EmptyState kind="error" title={t('adminCms:blockList.error.title')} action={{ label: tCommon('datatable:error.retry'), onClick: onRetry }} />
      )}

      {!isLoading && !isError && filteredGroups.length === 0 && (
        <EmptyState kind="first-use" title={t('adminCms:blockList.empty.title')} description={t('adminCms:blockList.empty.description')} />
      )}

      {!isLoading && !isError && filteredGroups.length > 0 && (
        <ul className={cn(styles['blockList'])}>
          {filteredGroups.map((group) => (
            <li key={group.blockKey}>
              <button type="button" className={cn(styles['blockRow'])} onClick={() => setOpenBlockKey(group.blockKey)}>
                <span className={cn(styles['blockKey'])}>{group.blockKey}</span>
                <span className={cn(styles['blockStatus'])}>{blockStatusLabel(group, t)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {openGroup && (
        <CmsBlockEditor
          blockKey={openGroup.blockKey}
          group={openGroup}
          onClose={() => setOpenBlockKey(null)}
          onChanged={invalidate}
        />
      )}
    </div>
    </RoleGate>
  );
}
